import { LinearProgress } from "@mui/material";
import { DataGridPro } from "@mui/x-data-grid-pro";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { gettingAssingedPaycodesInTotals, gettingTotals } from "utils/Timecard";
import { ToolbarTotals } from "./ToolbarTotal";

const Totals = ({
	open,
	range,
	setRange,
	employee,
	virtualization,
	virtualTotals,
}) => {
	const { t } = useTranslation();

	const [rows, setRows] = useState([]);
	const [totals, setTotals] = useState([]);
	const [paycodeTotals, setPaycodeTotals] = useState([]);

	const [loadingResults, setLoadingResults] = useState(false);
	const [loadingAssingedPaycodes, setLoadingGettingPaycodes] =
		useState(false);

	const columns = useMemo(
		() => [
			{
				field: "paycode_id",
				headerName: t(
					"timekeeper.timecard.audit_and_totals.table.columns.paycode_id"
				),
				flex: 0.4,
			},
			{
				field: "paycode",
				headerName: t(
					"timekeeper.timecard.audit_and_totals.table.columns.paycode"
				),
				flex: 1.4,
			},
			{
				field: "amount",
				headerName: t(
					"timekeeper.timecard.audit_and_totals.table.columns.amount"
				),
				flex: 0.2,
			},
		],
		[t]
	);

	const toMinutes = (hhmm) => {
		const [hh, mm] = hhmm.replace(/O/g, "0").split(":").map(Number);
		return hh * 60 + mm;
	};

	const toHHMM = (totalMinutes) => {
		const hours = String(Math.floor(totalMinutes / 60)).padStart(2, "0");
		const minutes = String(totalMinutes % 60).padStart(2, "0");
		return `${hours}:${minutes}`;
	};

	const reloading = useCallback(async () => {
		if (!employee?.id || !range?.[0] || !range?.[1]) {
			setRows([]);
			setTotals([]);
			setPaycodeTotals([]);
			return;
		}

		if (!virtualization) {
			await gettingTotals(
				setLoadingResults,
				setTotals,
				employee?.id,
				range[0].format("YYYY-MM-DD"),
				range[1].format("YYYY-MM-DD")
			);
		}

		await gettingAssingedPaycodesInTotals(
			setLoadingGettingPaycodes,
			employee,
			range,
			setPaycodeTotals
		);

		console.log("rows", rows);

		//eslint-disable-next-line
	}, [employee, range, virtualization]);

	const gettingRows = useCallback(() => {
		const array = virtualization
			? [...virtualTotals, ...paycodeTotals]
			: [...totals, ...paycodeTotals];

		console.log("array totals", array);

		const result = Object.values(
			array.reduce((acc, item) => {
				const key = item.paycode_id;
				const minutes = toMinutes(item.amount);

				if (!acc[key]) {
					acc[key] = {
						paycode_id: item.paycode_id,
						paycode: item.paycode,
						amount: 0,
					};
				}

				acc[key].amount += minutes;
				return acc;
			}, {})
		).map((item, key) => ({
			...item,
			id: key,
			amount: toHHMM(item.amount),
		}));

		console.log("results totals:", result);

		setRows(result);
	}, [paycodeTotals, totals, virtualTotals, virtualization]);

	useEffect(() => {
		setRows([]);

		if (open) {
			reloading();
		}
	}, [open, reloading]);

	useEffect(() => {
		setRows([]);

		if (!loadingAssingedPaycodes && !loadingResults) {
			gettingRows();
		}
	}, [gettingRows, loadingAssingedPaycodes, loadingResults]);

	return (
		<>
			<div style={{ height: "100%", width: "100%" }}>
				<DataGridPro
					rows={rows}
					columns={columns}
					slots={{
						toolbar: useCallback(
							() => (
								<ToolbarTotals
									reloading={reloading}
									range={range}
									setRange={setRange}
									loadingResults={loadingResults}
									loadingAssingedPaycodes={
										loadingAssingedPaycodes
									}
								/>
							),
							[
								loadingAssingedPaycodes,
								loadingResults,
								range,
								reloading,
								setRange,
							]
						),
						loadingOverlay: LinearProgress,
					}}
					//checkboxSelection
					disableRowSelectionOnClick
					loading={loadingResults || loadingAssingedPaycodes}
					hideFooter
					sx={{
						height: "365px",
					}}
				/>
			</div>
		</>
	);
};

export { Totals };
